/**
 * Permissions catalog (AUTH-CHAT-WIRING-CONTRACT §2.4).
 *
 * A read-only view of the RBAC permission catalog (the 31 `resource:action`
 * keys), grouped by resource. Backed by a direct `GET /api/admin/permissions`
 * (NOT a Refine resource, to avoid widening the `RESOURCES` registry). Mirrors
 * the shared `PageHeader` + `Table` design used across the resource lists, and
 * degrades to a calm labeled-empty / not-authorized state instead of a red
 * panel. The route element is also gated by `hasAdminAccess` so non-admins see
 * a friendly empty state rather than a crash (the backend already 403s the API).
 */
import * as React from "react";
import { usePermissions } from "@refinedev/core";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, LoadingState } from "@/components/ui/states";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  fetchPermissionsCatalog,
  groupByResource,
  type PermissionRow,
} from "@/lib/admin-api";
import { hasAdminAccess } from "@/lib/admin-access";
import { humanize } from "@/resources/projects/_form";

export function Permissions() {
  const { data: perms } = usePermissions<string[]>();
  const allowed = hasAdminAccess(perms);

  const [rows, setRows] = React.useState<PermissionRow[] | null>(null);
  const [errored, setErrored] = React.useState(false);

  React.useEffect(() => {
    if (!allowed) return;
    const controller = new AbortController();
    fetchPermissionsCatalog(controller.signal)
      .then((items) => setRows(items))
      .catch(() => setErrored(true));
    return () => controller.abort();
  }, [allowed]);

  if (!allowed) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Permissions"
          subtitle="The RBAC permission catalog (resource × action)."
          icon="roles"
        />
        <EmptyState
          icon="roles"
          title="Not authorized"
          hint="You need an admin permission to view the permission catalog."
        />
      </div>
    );
  }

  const groups = rows ? groupByResource(rows) : [];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Permissions"
        subtitle={
          rows
            ? `${rows.length} permission keys, grouped by resource. Read-only — assign these to roles or users on the Roles and Users pages.`
            : "The RBAC permission catalog (resource × action)."
        }
        icon="roles"
      />

      {errored ? (
        <EmptyState
          icon="roles"
          title="Catalog unavailable"
          hint="The permission catalog could not be loaded. Try again later."
        />
      ) : !rows ? (
        <LoadingState label="Loading permissions…" />
      ) : rows.length === 0 ? (
        <EmptyState
          icon="roles"
          title="No permissions"
          hint="The catalog is empty."
        />
      ) : (
        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.resource} className="space-y-3">
              <h2 className="text-sm font-semibold tracking-tight">
                {humanize(group.resource)}
                <span className="ml-2 text-xs font-normal text-muted-foreground">
                  {group.permissions.length}
                </span>
              </h2>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[28%]">Key</TableHead>
                      <TableHead className="w-[18%]">Action</TableHead>
                      <TableHead>Description</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {group.permissions.map((p) => (
                      <TableRow key={p.key}>
                        <TableCell>
                          <span className="font-mono text-xs">{p.key}</span>
                        </TableCell>
                        <TableCell>{humanize(p.action)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {p.description ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export default Permissions;
